import { sql } from "drizzle-orm";
import { check, index, integer, pgTable, text, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";
import { lessons, organisations, students, users } from "./schemaBase.js";
import { schoolNotes } from "./schoolCanonicalSchema.js";

const instant = (name: string) => timestamp(name, { withTimezone: true }).notNull().defaultNow();
const noteColumns = () => ({
  lessonId: varchar("lesson_id").references(() => lessons.id, { onDelete: "cascade" }),
  schoolNoteId: varchar("school_note_id").references(() => schoolNotes.id, { onDelete: "cascade" }),
});

/** Deployment date, so an absence of events never claims pre-tracking history. */
export const engagementTracking = pgTable("engagement_tracking", {
  organisationId: varchar("organisation_id").primaryKey().references(() => organisations.id, { onDelete: "cascade" }),
  startedAt: instant("started_at"),
});

/** One genuine display of a published note, with server-proved viewer identity. */
export const lessonNoteViewEvents = pgTable("lesson_note_view_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: varchar("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
  ...noteColumns(),
  studentId: varchar("student_id").notNull().references(() => students.id, { onDelete: "cascade" }),
  viewerUserId: varchar("viewer_user_id").references(() => users.id, { onDelete: "set null" }),
  viewerType: text("viewer_type").notNull(),
  eventKey: varchar("event_key").notNull(),
  noteUpdatedAt: timestamp("note_updated_at", { withTimezone: true }).notNull(),
  viewedAt: instant("viewed_at"),
}, (t) => [
  check("lesson_note_view_events_one_note", sql`num_nonnulls(${t.lessonId}, ${t.schoolNoteId}) = 1`),
  check("lesson_note_view_events_viewer_type", sql`${t.viewerType} in ('student', 'parent')`),
  uniqueIndex("lesson_note_view_events_retry_idx").on(t.organisationId, t.viewerUserId, t.eventKey),
  index("lesson_note_view_events_viewer_idx").on(t.organisationId, t.viewerUserId, t.viewedAt),
  index("lesson_note_view_events_private_idx").on(t.organisationId, t.lessonId, t.viewedAt),
  index("lesson_note_view_events_school_idx").on(t.organisationId, t.schoolNoteId, t.viewedAt),
]);

/** Structured staff assessment; a missing assessment is unknown, never zero. */
export const lessonWorkFeedback = pgTable("lesson_work_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: varchar("organisation_id").notNull().references(() => organisations.id, { onDelete: "cascade" }),
  ...noteColumns(),
  authorUserId: varchar("author_user_id").references(() => users.id, { onDelete: "set null" }),
  effortRating: integer("effort_rating"),
  progressRating: integer("progress_rating"),
  comment: text("comment").notNull().default(""),
  version: integer("version").notNull().default(1),
  updatedAt: instant("updated_at"),
}, (t) => [
  check("lesson_work_feedback_one_note", sql`num_nonnulls(${t.lessonId}, ${t.schoolNoteId}) = 1`),
  check("lesson_work_feedback_effort", sql`${t.effortRating} between 1 and 5`),
  check("lesson_work_feedback_progress", sql`${t.progressRating} between 1 and 5`),
  check("lesson_work_feedback_version", sql`${t.version} > 0`),
  uniqueIndex("lesson_work_feedback_private_idx").on(t.organisationId, t.lessonId),
  uniqueIndex("lesson_work_feedback_school_idx").on(t.organisationId, t.schoolNoteId),
]);

export type NoteDomain = "private" | "school";
export interface EngagementAccount {
  label: string;
  relationship: "student" | "parent";
  lastLoginAt: string | null;
}
export interface NoteViewSummary {
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  studentLastViewedAt: string | null;
  parentLastViewedAt: string | null;
  viewCount: number;
  uniqueViewers: number;
  currentVersionViewed: boolean;
}
export interface NoteViewEvent {
  /** Stable identity is absent only when reading a Hub deployed before this contract. */
  id?: string;
  relationship: "student" | "parent";
  label: string;
  viewedAt: string;
}
export interface NoteViewHistoryEvent extends NoteViewEvent {
  /** New continuation pages always include identity for stable keys and deduplication. */
  id: string;
}
export interface NoteViewHistoryPage {
  views: NoteViewHistoryEvent[];
  historyHasMore: boolean;
  historyNextCursor: string | null;
}
export interface EngagementNote extends NoteViewSummary {
  id: string;
  domain: NoteDomain;
  title: string;
  lessonAt: string | null;
  publishedAt: string;
}
export interface StudentEngagement {
  trackingStartedAt: string | null;
  accounts: EngagementAccount[];
  publishedNotes: number;
  viewedNotes: number;
  notes: EngagementNote[];
}
export interface WorkFeedback {
  effortRating: number | null;
  progressRating: number | null;
  comment: string;
  updatedAt: string;
  version: number;
}
export interface NoteEngagement extends NoteViewSummary {
  trackingStartedAt: string | null;
  publishedAt: string | null;
  accounts: EngagementAccount[];
  views: NoteViewEvent[];
  historyHasMore: boolean;
  /** Absent only when reading a Hub deployed before cursor continuation. */
  historyNextCursor?: string | null;
  feedback: WorkFeedback | null;
  work: WorkSinceLesson | null;
}

/** Raw inputs, intentionally without a combined score or inferred assessment. */
export interface WorkSinceLesson {
  from: string;
  to: string;
  nextLessonAt: string | null;
  linkedTasks: number;
  completedTasks: number;
  practiceSeconds: number;
  practiceDays: number;
  practiceSessions: number;
  autoStoppedSessions: number;
  attribution: "note-linked-tasks";
}

export interface EngagementReportStats {
  publishedNotes: number;
  viewedNotes: number;
  students: number;
  engagedStudents: number;
  studentViewedNotes: number;
  parentViewedNotes: number;
  notesWithoutAccount: number;
  averageSecondsToView: number | null;
}
export interface EngagementReport {
  trackingStartedAt: string | null;
  schoolAvailable: boolean;
  totals: EngagementReportStats;
  tutors: (EngagementReportStats & { key: string; name: string })[];
  students: (EngagementReportStats & { id: string; name: string; domain: NoteDomain; school: string | null; hasAccount: boolean })[];
  totalStudents: number;
  page: number;
  pageSize: number;
  /** Server-computed page count for `totalStudents` at `pageSize`; clients must not recompute it from their own constant. */
  totalPages: number;
  filters: { tutors: { key: string; name: string }[]; schools: string[] };
}
